<script setup>
import { computed, defineAsyncComponent, watch } from 'vue';
import FormGroup from '@maison/components/form/FormGroup.vue';
import RadioListField from '@maison/components/form/fields/RadioListField.vue';
import CheckboxListField from '@maison/components/form/fields/CheckboxListField.vue';
import { useI18n } from 'vue-i18n';
import { clientApi } from '@phoenix/clientApi';
import { get } from 'lodash-es';
import { showModal } from '@phoenix/helpers/modal-helper';

const PhoenixForm = defineAsyncComponent(() => import('@maison/components/form/PhoenixForm.vue'));

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
  form: {
    type: Object,
    required: true,
  },
  beforeSubmit: {
    type: Function,
    default: () => true,
  },
  successModalId: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const fields = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
  },
});

const termsLink = computed(() =>
  t('Form.agree', {
    privacyLink: `<a target='_blank' class='rcms_privacypol link link--text' href='${props.form.termsLink}'>${t(
      'Form.privacy',
    )}</a>`,
  }),
);

const isPartner = computed(() => fields.value.partner.value);

const shouldDisplayBoutiqueSelector = computed(() => !isPartner.value && fields.value.contactType.value === 'boutique');

const hasBoutiques = computed(() => fields.value.boutique.options.length > 0);

const isSubmitDisabled = computed(() => shouldDisplayBoutiqueSelector.value && !hasBoutiques.value);

const recaptchaTerms = computed(() =>
  t('EngravingTool.recaptchaTerms.text', {
    privacyLink: `<a href="https://policies.google.com/privacy" target="_blank" class="link link--text">${t(
      'EngravingTool.recaptchaTerms.privacy',
    )}</a>`,
    termsLink: `<a href="https://policies.google.com/terms" target="_blank" class="link link--text">${t(
      'EngravingTool.recaptchaTerms.terms',
    )}</a>`,
  }),
);

const fetchBoutiques = async (country) => {
  try {
    const { data } = await clientApi.get(window.route('api.engravingTool.getBoutiques'), {
      params: { country },
    });

    fields.value.boutique.options = data.data.boutiques.map((boutique) => ({
      id: `boutique-${boutique.id}`,
      value: boutique.id.toString(),
      label: boutique.name,
      data: boutique,
    }));

    fields.value.boutique.value = fields.value.boutique.options[0]?.value ?? null;
  } catch (error) {
    window.Phoenix.sentry.captureException(error);
    window.Phoenix.toaster.notify({
      type: 'danger',
      text: get(error, 'response.data.message', error.message),
    });
  }
};

const onSuccess = () => {
  if (props.form.redirectUrl) {
    return;
  }

  showModal(props.successModalId);
};

watch(
  () => fields.value.country.value,
  (value) => {
    fetchBoutiques(value);
  },
);
</script>

<template>
  <div v-if="!isPartner" class="text--muted">{{ $t('EngravingTool.checkout.contactPointSubtitle') }}</div>

  <phoenix-form
    v-model="fields"
    :action="form.action"
    :captcha-enabled="form.captchaEnabled"
    :terms-link="termsLink"
    :is-contained="false"
    :custom-submit-text="$t('EngravingTool.checkout.submit')"
    :before-submit="beforeSubmit"
    :redirect-url="form.redirectUrl"
    :is-submit-disabled="isSubmitDisabled"
    :show-success-in-toast="false"
    :on-success-callback="onSuccess"
    class="mt-base"
  >
    <template #default>
      <div class="flex-grid flex-grid--y--base flex-grid--x--base">
        <template v-if="!isPartner">
          <form-group :attrs="fields.contactType" class="w-full">
            <radio-list-field v-model="fields.contactType.value" inline />
          </form-group>

          <template v-if="shouldDisplayBoutiqueSelector">
            <form-group v-if="hasBoutiques" :attrs="fields.boutique" class="w-full">
              <radio-list-field v-model="fields.boutique.value">
                <template #label="{ field, fieldId, classes }">
                  <label :for="fieldId" :class="classes">
                    <address>
                      <strong>{{ field.data.name }}</strong> <br />
                      {{ field.data.address1 }}<br />
                      <template v-if="field.data.address2"> {{ field.data.address2 }}<br /> </template>
                      {{ field.data.zip }} {{ field.data.city }}<br />
                      {{ field.data.country }}
                    </address>
                  </label>
                </template>
              </radio-list-field>
            </form-group>

            <div v-else class="text--muted">{{ $t('EngravingTool.checkout.noBoutique') }}</div>
          </template>
        </template>

        <form-group :attrs="fields.contactMode" class="w-full">
          <checkbox-list-field v-model="fields.contactMode.value" inline />
        </form-group>
      </div>
    </template>

    <template #after-terms>
      <p v-html="recaptchaTerms" />
    </template>
  </phoenix-form>
</template>
